import { mengenpostenRechnen, mengensatzFinden } from "@mrmachine/fach";
import type { Mengenpreisfrage } from "@mrmachine/fach";
import { postenmenge } from "./postenmenge";
import type { Leistungsart } from "./typen";

/* ══════════════════════════════════════════════════════════════════
   Der Satz einer Leistung, wie die Annahme ihn einfrieren wird (P8-05).

   Prüfrunde 05.10.2026: Die PV-Reinigung stand in „Vermieten" mit
   „1,20 je m²" — dem Stammsatz. Die Leistungsart führt aber drei
   Verschmutzungsgrade mit eigenen Sätzen; ohne gewählten Grad fror die
   Annahme **keinen** Mengenpreis ein, und AB und Lauf rechneten 0,00. Eine
   Zahl an zwei Stellen verschieden.

   **Anzeige und Schnappschuss aus derselben Quelle**: Hier rechnet dieselbe
   Funktion wie in der API (`fach.mengensatzFinden`, `dienste/mengensatz.ts`)
   mit den Stammdaten, die die Liste ohnehin hat. Was sie sagt, steht in der
   Liste und in der Merkliste, und genau das friert die Annahme ein:

   - keine Grade → der Stammsatz (oder die Staffel), wie bisher;
   - **genau ein Grad** oder eine **Vorgabe** → dieser, ohne Frage (Regel 11);
   - **mehrere ohne Vorgabe** → kein Satz, bis jemand wählt. „Jetzt
     vermieten" hält dann an (`annahme.grad_fehlt` in der API dahinter).
   ══════════════════════════════════════════════════════════════════ */

export type Leistungssatz = {
  /** Der Satz je Einheit — `null`: steht (noch) keiner fest. */
  satz: number | null;
  /** Der Grad, mit dem gerechnet wurde — gewählt, Vorgabe oder der einzige. */
  kategorieId: string | null;
  /** Mehrere Grade, keine Vorgabe, keiner gewählt: Die Wahl fehlt. */
  wahlNoetig: boolean;
  /** Die Grade zur Wahl, in ihrer Reihenfolge. Leer: Die Leistung führt keine. */
  grade: { id: string; label: string }[];
};

/** Die Frage an `fach` aus dem Stamm, den die Liste ohnehin hat. */
function frageAus(l: Leistungsart, kategorieId: string | null, menge: number): Mengenpreisfrage {
  return {
    grundpreis: l.netto,
    saetze: l.saetze.map((s) => ({
      id: s.id, variant_id: s.kategorieId || null, from_quantity: s.abMenge, price_net: s.netto,
    })),
    kategorien: l.kategorien.map((k) => ({ id: k.id, code: k.code, label: k.label })),
    kategorie_id: kategorieId,
    vorgabe_kategorie_id: l.vorgabeKategorieId,
    menge: Math.max(0, menge),
  };
}

export function leistungssatz(l: Leistungsart, kategorieId: string | null, menge: number): Leistungssatz {
  const grade = [...l.kategorien]
    .sort((a, b) => a.sortierung - b.sortierung)
    .map((k) => ({ id: k.id, label: k.label || k.code }));
  const antwort = mengensatzFinden(frageAus(l, kategorieId, menge));
  return {
    satz: antwort.satz,
    kategorieId: antwort.kategorie?.id ?? null,
    wahlNoetig: antwort.fehlt === "kategorie_fehlt",
    grade,
  };
}

/**
 * **Eine Menge an einer Leistung in der Merkliste** (08.10.2026).
 *
 * Luca: *„… auch direkt die Quadratmeterzahl eintragen können, sodass ich das
 * auch direkt vermieten kann."* Gerechnet wird mit `fach.mengenpostenRechnen`
 * — derselben Mindestmenge und derselben Mengenstufe, mit denen die API die
 * Position anlegt und die Annahme den Satz einfriert. Die Merkliste trägt
 * danach, was der Auftrag tragen wird: die **angehobene** Menge und den Satz
 * ihrer Stufe.
 *
 * Leer oder 0 heißt an einer Leistung mit Aufmaß „Menge folgt aus dem Aufmaß"
 * (0); an jeder anderen bleibt mindestens eins (`postenmenge`). Ohne Stamm
 * (Leistung von Hand) bleibt der Satz, wie er war.
 */
export type Leistungsmenge = {
  /** Die Menge, die der Posten trägt — angehoben, wenn eine Mindestmenge greift. */
  menge: number;
  /** Der Satz je Einheit zu dieser Menge — `null`: steht (noch) keiner fest. */
  satz: number | null;
  /** Hat die Mindestmenge angehoben? Dann sagt die Maske es dazu. */
  angehoben: boolean;
  /** Die Mindestmenge der Leistungsart, wenn eine gepflegt ist. */
  mindestmenge: number | null;
};

export function leistungsmenge(
  l: Leistungsart | null,
  kategorieId: string | null,
  eingabe: number | undefined,
  aufmass: boolean,
): Leistungsmenge {
  const genannt = postenmenge("leistung", eingabe, aufmass);
  if (!l) return { menge: genannt, satz: null, angehoben: false, mindestmenge: null };
  const mindestmenge = l.mindest_menge !== null && l.mindest_menge > 0 ? l.mindest_menge : null;
  const posten = mengenpostenRechnen({ ...frageAus(l, kategorieId, genannt), mindestmenge });
  return { menge: posten.menge, satz: posten.satz, angehoben: posten.angehoben, mindestmenge };
}
